import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./BuildProgress.scss";

import th1 from "../assets/buildProgress/threehalves/th1.webp";
import th2 from "../assets/buildProgress/threehalves/th2.webp";
import th3 from "../assets/buildProgress/threehalves/th3.webp";
import th4 from "../assets/buildProgress/threehalves/th4.webp";
import bodyFEA from "../assets/buildProgress/threehalves/BodyFEA.webp";
import pullRodFEA from "../assets/buildProgress/threehalves/PullRodFEA.webp";
import boomsaw1 from "../assets/buildProgress/boomsaw/boomsaw1.webp";
import boomsaw2 from "../assets/buildProgress/boomsaw/boomsaw2.webp";
import boomsaw3 from "../assets/buildProgress/boomsaw/boomsaw3.webp";

// A nested array shares one grid cell, stacked vertically. Two landscape shots
// in one cell sit better beside a portrait than either would on its own.
const SECTIONS = [
  {
    title: "THREEHALVES",
    images: [th1, th2, [th3, th4], bodyFEA, pullRodFEA],
  },
  {
    title: "BOOM SAW",
    images: [boomsaw1, boomsaw2, boomsaw3],
  },
];

// Matches the two-column breakpoint in BuildProgress.scss. Below it an image
// already fills the width of the screen, so there is nothing to zoom into.
const ZOOMABLE_QUERY = "(min-width: 750px)";

function useZoomable() {
  const [zoomable, setZoomable] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(ZOOMABLE_QUERY);
    const update = () => setZoomable(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return zoomable;
}

function Image({ src, zoomable, onZoom }) {
  return (
    <button
      className="buildProgressZoomButton"
      type="button"
      disabled={!zoomable}
      aria-label="view larger"
      onClick={() => onZoom(src)}
    >
      <img
        className="buildProgressImage"
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
      />
    </button>
  );
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="enlarged image"
      onClick={onClose}
    >
      <button className="lightboxClose" type="button" aria-label="close">
        ×
      </button>
      <img className="lightboxImage" src={src} alt="" />
    </div>
  );
}

function BuildProgress() {
  const zoomable = useZoomable();
  const [zoomedSrc, setZoomedSrc] = useState(null);
  const closeZoom = useCallback(() => setZoomedSrc(null), []);

  // Shrinking past the breakpoint while zoomed should drop back to the grid.
  useEffect(() => {
    if (!zoomable) {
      setZoomedSrc(null);
    }
  }, [zoomable]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Satyress — Build Progress";

    // Keep this page out of search results.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    return () => {
      document.title = previousTitle;
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="buildProgress">
      <header className="buildProgressHeader">
        <Link className="buildProgressBack" to="/">
          SATYRESS
        </Link>
        <h1 className="buildProgressTitle">BUILD PROGRESS</h1>
      </header>

      {SECTIONS.map((section) => (
        <section className="buildProgressSection" key={section.title}>
          <h2 className="buildProgressSectionTitle">{section.title}</h2>
          <div className="buildProgressGrid">
            {section.images.map((image) =>
              Array.isArray(image) ? (
                <div className="buildProgressStack" key={image[0]}>
                  {image.map((src) => (
                    <Image
                      src={src}
                      key={src}
                      zoomable={zoomable}
                      onZoom={setZoomedSrc}
                    />
                  ))}
                </div>
              ) : (
                <Image
                  src={image}
                  key={image}
                  zoomable={zoomable}
                  onZoom={setZoomedSrc}
                />
              ),
            )}
          </div>
        </section>
      ))}

      {zoomedSrc && <Lightbox src={zoomedSrc} onClose={closeZoom} />}
    </div>
  );
}

export default BuildProgress;
